<template>
    <ModalDialog
        :z-index="1025"
        style="z-index: 1026;"
        ref="modalDialog"
        @close="reset"
        @opened="modalOpened"
    >
        <template v-slot:title>
            {{ isEdit ? "Edit Item" : "New Item" }}
        </template>
        <template v-slot:body>
            <div class="form-group form-row">
                <label for="item" class="col-md-4 col-form-label">Item:</label>
                <div class="col-md-8">
                    <vet-select
                        ref="vet-select-item"
                        id="item"
                        placeholder="Inventory Item (required)"
                        v-model="form.item"
                        name="item"
                        module="wellness-element"
                        v-validate="'required'"
                        :class="{ 'border-danger': errors.has('item') }"
                    />
                </div>
            </div>
            <div class="form-group form-row">
                <label for="list-price" class="col-md-4 col-form-label"
                    >List Price:</label
                >
                <div class="col-md-8 col-form-label">
                    <span v-if="form.item">{{
                        `${_toCurrency(form.item.price)} / ${
                            form.item.dose
                        } ${itemUOM}`
                    }}</span
                    ><span v-else>{{ _toCurrency(0) }} / 1 Unit(s)</span>
                </div>
            </div>
            <div class="form-group form-row">
                <label for="quantity" class="col-md-4 col-form-label"
                    >Quantity:</label
                >
                <div class="col-8 col-md-4">
                    <input
                        type="number"
                        id="quantity"
                        name="quantity"
                        v-model="form.quantity"
                        placeholder="Quantity (required)"
                        v-validate="
                            'required|min_value:0|numericalWithDecimal|decimal:3'
                        "
                        class="form-control"
                        :class="{
                            'border-danger': errors.has('quantity')
                        }"
                    />
                </div>
            </div>
            <div v-if="isAbleUpdateProviderCredit" class="form-group form-row">
                <label for="provider-credit" class="col-md-4 col-form-label"
                    >Provider Credit:</label
                >
                <div class="col-8 col-md-4">
                    <input
                        type="number"
                        id="provider-credit"
                        name="provider_credit"
                        v-model="form.providerCredit"
                        placeholder="Provider Credit (optional)"
                        v-validate="'required|numericalWithDecimal|decimal:2'"
                        class="form-control"
                        :class="{
                            'border-danger': errors.has('provider_credit')
                        }"
                    />
                </div>
                <div class="col col-form-label">
                    %
                </div>
            </div>
        </template>
        <template v-slot:confirmBtn>
            <div class="ml-auto">
                <div v-if="!isSaving">
                    <b-button-group>
                        <b-dropdown
                            left
                            split
                            :text="isAddNew ? 'Save + Add New' : 'Save + Done'"
                            variant="primary"
                            :disabled="false"
                            @click="save(isAddNew)"
                        >
                            <b-dropdown-item @click="save(!isAddNew)">
                                {{
                                    isAddNew ? "Save + Done" : "Save + Add New"
                                }}
                            </b-dropdown-item>
                        </b-dropdown>
                    </b-button-group>
                </div>
                <loader v-else :margin="1"></loader>
            </div>
        </template>
    </ModalDialog>
</template>

<script>
import ModalDialog from "@/components/commons/modal-dialog";
import VetSelect from "@/module/commons/vet-select";
import TemplateService from "@/services/templates";
import TEMPLATES from "@/constants/templates";
import Loader from "@/module/commons/loader";

const initialForm = () => ({
    id: null,
    item: null,
    quantity: 1,
    providerCredit: 0
});

export default {
    components: {
        ModalDialog,
        VetSelect,
        Loader
    },
    data() {
        return {
            isEdit: false,
            isAddNew: false,
            form: initialForm(),
            isSaving: false
        };
    },
    computed: {
        templateId() {
            return this.$store.state.settingsTemplate.templateId;
        },
        details() {
            return this.$store.state.settingsTemplate.details;
        },
        itemUOM() {
            if (!this.form.item) {
                return "UOM";
            }

            // UOM from existing item
            if (this.form.item.measurement_unit) {
                return this.form.item.measurement_unit.value;
            }

            // UOM from search item list
            return this.form.item.uom;
        },
        isAbleUpdateProviderCredit() {
            let productionCredit = this.details?.template_attribute
                ?.production_credit;

            if (!productionCredit) {
                return false;
            }

            return [
                TEMPLATES.PRODUCTION_CREDIT_TYPE.ENROLLMENT_CREDIT,
                TEMPLATES.PRODUCTION_CREDIT_TYPE.SERVICE_CREDIT
            ].includes(productionCredit);
        }
    },
    mounted() {},
    methods: {
        focusOnItemInput() {
            this.$refs["vet-select-item"].$el.querySelector("input").focus();
        },
        async open(isEdit, formData) {
            this.isEdit = isEdit;

            if (this.isEdit) {
                this.form.id = formData.id;
                this.form.item = formData.item;
                this.form.item.label = formData.item.name;
                this.form.quantity = formData.quantity;
                this.form.providerCredit = formData.provider_credit;
            }

            this.$refs.modalDialog.open();
        },
        reset() {
            this.isEdit = false;
            this.form = Object.assign({}, initialForm());
            this.$validator.reset();
        },
        close() {
            this.reset();
            this.$refs.modalDialog.close();
            this.$emit("close");
        },
        modalOpened() {
            if (this.isEdit) {
                return;
            }

            this.focusOnItemInput();
        },
        async save(isAddNew = false) {
            let isValid = await this.$validator.validate();

            if (!isValid) {
                return;
            }

            try {
                this.isSaving = true;

                const payload = {
                    id: this.form.id,
                    itemid: this.form.item.id,
                    quantity: this.form.quantity,
                    provider_credit: this.form.providerCredit
                };
                let res = this.isEdit
                    ? await TemplateService.updateWellnessPlanItem(
                          this.templateId,
                          payload
                      )
                    : await TemplateService.addWellnessPlanItem(
                          this.templateId,
                          payload
                      );

                if (res) {
                    this._message(
                        this.isEdit
                            ? "Edits successfully saved"
                            : "Item successfully added"
                    );
                    this.$emit("onsuccess");
                    this.isAddNew = isAddNew;

                    if (isAddNew) {
                        this.reset();
                    } else {
                        this.close();
                    }
                }
            } catch (error) {
                console.error(error);
                this._message("Error saving data", "danger");
            } finally {
                this.isSaving = false;
            }
        }
    }
};
</script>
